import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  Space,
  Card,
  Dropdown,
  Typography,
  Drawer,
  Form,
  Input,
  Select,
  Upload,
  Row,
  Col,
} from "antd";
import { Button, Spinner } from "reactstrap";
import Swal from "sweetalert2";
import {
  DownOutlined,
  UploadOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Icon } from "../../../components/Component";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import TopMenu from "../../../route/topMenu";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import { documentApis } from "../../../api/Documents";
import { tasksApis } from "../../../api/NewTasks";
import { issueApis } from "../../../api/Issue";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import { CancelAccess } from "../../../components/accessControl/AccessControl";
import { useAccessibility, canAdd } from "../../../utils/accessControl";
import SearchInput from "../../form-components/SearchComponent";
import { SearchOutlined } from "@ant-design/icons";
import { FormProvider, useForm } from "react-hook-form";
import { useWbsName } from "../shared/useWbsCodes";

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface DocumentData {
  paginationId: number;
  docId: number;
  customDocName: string;
  linkType: string;
  linkTo: string;
  remarks: string;
  filePath: string;
}

const DocumentPage: React.FC = () => {
  // Add minimal CSS for hover effects and form styling
  React.useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .file-row-hover:hover .delete-icon,
      .file-row-hover:hover .download-icon {
        opacity: 1 !important;
        transform: scale(1.1) !important;
      }
      .delete-icon, .download-icon {
        opacity: 0.3 !important;
        transition: all 0.2s ease !important;
      }
      .delete-icon { color: #ff4d4f !important; }
      .download-icon { color: #52c41a !important; }
      .ant-upload, .ant-upload .ant-btn, .ant-form-item-control-input, .ant-input {
        width: 100% !important;
      }
      .ant-upload-list-item-actions .anticon-delete,
      .ant-upload-list-item-actions .ant-btn {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
      }
      .ant-input[disabled] {
        background-color: #f5f5f5 !important;
        color: #262626 !important;
        cursor: not-allowed !important;
        border-color: #d9d9d9 !important;
      }
      
      /* Disabled Select components styling to match Input styling */
      .ant-select-disabled .ant-select-selector {
        background-color: #f5f5f5 !important;
        color: #262626 !important;
        cursor: not-allowed !important;
        border-color: #d9d9d9 !important;
      }
      
      .ant-select-disabled .ant-select-selection-item {
        color: #262626 !important;
      }
      
      /* Simple disabled document name styling */
      .disabled-doc-name.ant-input[disabled] {
        background-color: #f5f6fa !important;
        color: #3c4d62 !important;
      }
      
      .disabled-doc-name.ant-input[disabled] input {
        background-color: #f5f6fa !important;
        color: #3c4d62 !important;
      }

    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Add screen size management
  const [screenSize, setScreenSize] = useState(0);

  // Function to handle screen size changes
  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  // Add useEffect for screen size management
  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  const [groupBy, setGroupBy] = useState<string>("projects");
  const currentGroupByRef = useRef<string>("projects");
  const isInitialLoadRef = useRef<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isAddingToGroup, setIsAddingToGroup] = useState(false);
  const [form] = Form.useForm();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const methods = useForm();

  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [dataSource, setDataSource] = useState<DocumentData[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    defaultCurrent: 1,
    defaultPageSize: 15,
    showSizeChanger: true,
    pageSizeOptions: ["15", "20", "30", "40", "50"],
    total: 0,
  });
  const location = useLocation();
  const { id, derivedProjectStatus, projectName } = location.state || {};

  const dispatch = useDispatch();
  const wbsName = useWbsName();

  // Move useAccessibility to the top level of the component
  const accessibility = useAccessibility();

  // Group by options with more descriptive labels
  const groupByOptions = [
    { key: "projects", label: "Project Documents", moduleName: "Projects" },
    { key: "phases", label: "Phase Documents", moduleName: "Phases" },
    {
      key: "activities",
      label: "Activity Documents",
      moduleName: "Activities",
    },
    { key: "tasks", label: "Task Documents", moduleName: "Tasks" },
    { key: "issues", label: "Issue Documents", moduleName: "Issues" },
  ];

  // Link type options
  const linkTypeOptions = [
    { value: "project", label: "Project" },
    { value: "phase", label: "Phase" },
    { value: "activity", label: "Activity" },
    { value: "task", label: "Task" },
    { value: "issue", label: "Issue" },
  ];

  // Function to group documents by linkType, linkTo, and document name
  const groupDocumentsByLink = (documents: any[]) => {
    const groupedMap = new Map();

    documents.forEach((doc) => {
      // Create group key based on linkType, linkTo, and document name
      const docName = doc.customDocName?.trim() || doc.docName?.trim() || "";
      const groupKey = `${doc.linkType}_${doc.linkTo}_${docName}`;

      if (groupedMap.has(groupKey)) {
        // If group exists, add this document to the group
        const existingGroup = groupedMap.get(groupKey);
        existingGroup.documents.push(doc);
        existingGroup.docCount = existingGroup.documents.length;

        // Show the document name (since all documents in group have same name)
        existingGroup.docNames = docName;
        existingGroup.customDocName = docName;

        // Preserve existing remarks if new document doesn't have remarks
        if (!doc.remarks && existingGroup.remarks) {
          // Keep the existing remarks from the group
        } else if (doc.remarks && !existingGroup.remarks) {
          // If new document has remarks but group doesn't, update group remarks
          existingGroup.remarks = doc.remarks;
        }
        // If both have remarks, keep the existing group remarks (first document's remarks)
      } else {
        // Create new group
        groupedMap.set(groupKey, {
          ...doc,
          documents: [doc],
          docCount: 1,
          docNames: docName,
          customDocName: docName,
          key: groupKey,
        });
      }
    });

    const result = Array.from(groupedMap.values());
    return result;
  };

  // Get Link To options based on Link Type, excluding already selected options
  const getLinkToOptions = (linkType: string, index: number) => {
    // Get all currently selected linkTo values from other documents
    const currentValues = form.getFieldsValue();
    const documents = currentValues.documents || [];
    const selectedLinkTos = documents
      .map((doc: any, i: number) => (i !== index ? doc.linkTo : null))
      .filter((value: any) => value !== null && value !== undefined);

    // Get the current document's selected value to include it in options
    const currentDocumentValue = documents[index]?.linkTo;

    switch (linkType) {
      case "project":
        // Use real project list from API, show all projects
        return projectList.map((project: any) => ({
          value: project.projectId.toString(),
          label: project.projectTitle,
        }));
      case "phase":
        // For phase, show phases list if groupBy is phases, otherwise show project list
        if (groupBy === "phases") {
          const availablePhases = phasesList
            .filter(
              (phase: any) =>
                !selectedLinkTos.includes(phase.phaseId.toString()) ||
                phase.phaseId.toString() === currentDocumentValue
            )
            .map((phase: any) => ({
              value: phase.phaseId.toString(),
              label: wbsName(phase.phaseName),
            }));

          // If no phases are available, show a message
          if (availablePhases.length === 0) {
            if (phasesList.length === 0) {
              return [
                {
                  value: "no_phases",
                  label: "No phases available for this project",
                  disabled: true,
                },
              ];
            } else {
              return [
                {
                  value: "all_selected",
                  label: "All phases already selected",
                  disabled: true,
                },
              ];
            }
          }

          return availablePhases;
        } else {
          // For phase, always show project list in the first field
          return projectList.map((project: any) => ({
            value: project.projectId.toString(),
            label: project.projectTitle,
          }));
        }
      case "activity":
        const availableActivities = activityList
          .filter(
            (activity: any) =>
              !selectedLinkTos.includes(activity.activityId.toString()) ||
              activity.activityId.toString() === currentDocumentValue
          )
          .map((activity: any) => ({
            value: activity.activityId.toString(),
            label: wbsName(activity.activityName),
          }));

        // If no activities are available, show a message
        if (availableActivities.length === 0) {
          if (activityList.length === 0) {
            return [
              {
                value: "no_activities",
                label: "No activities available for this project",
                disabled: true,
              },
            ];
          } else {
            return [
              {
                value: "all_selected",
                label: "All activities already selected",
                disabled: true,
              },
            ];
          }
        }

        return availableActivities;
      case "task":
        const availableTasks = taskList
          .filter(
            (task: any) =>
              !selectedLinkTos.includes(task.taskId.toString()) ||
              task.taskId.toString() === currentDocumentValue
          )
          .map((task: any) => ({
            value: task.taskId.toString(),
            label: task.taskName,
          }));

        // If no tasks are available, show a message
        if (availableTasks.length === 0) {
          if (taskList.length === 0) {
            return [
              {
                value: "no_tasks",
                label: "No tasks available for this project",
                disabled: true,
              },
            ];
          } else {
            return [
              {
                value: "all_selected",
                label: "All tasks already selected",
                disabled: true,
              },
            ];
          }
        }

        return availableTasks;
      case "issue":
        const availableIssues = issueList.filter(
          (issue: any) =>
            !selectedLinkTos.includes(issue.issueId.toString()) ||
            issue.issueId.toString() === currentDocumentValue
        );

        // If no issues are available, show a message
        if (availableIssues.length === 0) {
          if (issueList.length === 0) {
            return [
              {
                value: "no_issues",
                label: "No issues available for this project",
                disabled: true,
              },
            ];
          } else {
            return [
              {
                value: "all_selected",
                label: "All issues already selected",
                disabled: true,
              },
            ];
          }
        }

        return availableIssues.map((issue: any) => ({
          value: issue.issueId.toString(),
          label: issue.issueName,
        }));
      default:
        return [];
    }
  };

  // Helper function to get label
  const getLabel = (list: any[], key: string) =>
    list.find((item) => item.key === key)?.label || key;

  // Helper function to filter dropdown items based on access permissions
  const getFilteredGroupByOptions = () => {
    // Return all options without filtering - keep all dropdown options visible
    return groupByOptions;
  };

  // Helper function to get display name for Link To column
  const getLinkToDisplayName = (linkType: string, linkTo: string) => {
    switch (linkType) {
      case "project":
        return projectNameMapping[linkTo] || linkTo;
      case "phase":
        return phaseNameMapping[linkTo] || linkTo;
      case "activity":
        return activityNameMapping[linkTo] || linkTo;
      case "task":
        return taskNameMapping[linkTo] || linkTo;
      case "issue":
        return issueNameMapping[linkTo] || linkTo;
      default:
        return linkTo;
    }
  };

  // Helper function to get phase name
  const getPhaseName = async (phaseId: string, projectId: number) => {
    try {
      const phaseResponse = await documentApis.phaseList({ projectId });
      let phaseData = phaseResponse;
      if (
        phaseResponse &&
        typeof phaseResponse === "object" &&
        phaseResponse.data
      ) {
        phaseData = phaseResponse.data;
      }
      if (Array.isArray(phaseData)) {
        const phase = phaseData.find(
          (p: any) => p.phaseId.toString() === phaseId
        );
        return phase ? phase.phaseName : null;
      }
    } catch (error) {
      console.error("Error getting phase name:", error);
    }
    return null;
  };

  // Function to download document
  const downloadDocument = (filePath: string, fileName: string) => {
    try {
      // Force download by using fetch and blob
      fetch(filePath)
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName || "document";
          link.style.display = "none";

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up
          window.URL.revokeObjectURL(url);
        })
        .catch(() => {
          // Fallback to direct download
          const link = document.createElement("a");
          link.href = filePath;
          link.download = fileName || "document";
          link.target = "_blank";
          link.style.display = "none";

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
    } catch (error) {
      // Final fallback - open in new tab
      window.open(filePath, "_blank");
    }
  };

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => {
          return {
            ...item,
            linkState: location.state,
          };
        })
      )
    );
  }, [location]);

  const showModal = () => {
    setIsModalVisible(true);

    // Pre-select values based on groupBy
    let initialLinkType = undefined;
    let initialLinkTo = undefined;

    if (groupBy === "projects") {
      initialLinkType = "project";
      initialLinkTo = projectName; // Use project name from location.state if available
    } else if (groupBy === "phases") {
      initialLinkType = "phase";
      // For phases, we'll show phases list in Link To
      initialLinkTo = undefined;
    } else if (groupBy === "activities") {
      initialLinkType = "activity";
      // For activities, we'll show activity selection
      initialLinkTo = undefined;
    } else if (groupBy === "tasks") {
      initialLinkType = "task";
      // For tasks, we'll show the task selection
      initialLinkTo = undefined;
    } else if (groupBy === "issues") {
      initialLinkType = "issue";
      // For issues, we'll show the issue selection
      initialLinkTo = undefined;
    }

    // Ensure form is properly initialized before setting values
    try {
      form.resetFields();
      form.setFieldsValue({
        documents: [
          {
            linkType: initialLinkType,
            linkTo: initialLinkTo,
            project: groupBy === "phases" ? id?.toString() : undefined, // Pre-select current project for phases
            phase: undefined,
            docName: "",
            remarks: "",
            file: undefined,
            selectedProjectId: groupBy === "phases" ? id : undefined,
          },
        ],
      });
    } catch (error) {
      console.error("Error initializing form:", error);
      // Fallback to basic initialization
      form.resetFields();
    }

    setSelectedProjectForPhase(groupBy === "phases" ? id : null);

    // If Group By Phases, fetch phases for the current project
    if (groupBy === "phases" && id) {
      fetchPhaseList(id);
    }

    // If Group By Tasks, fetch tasks for the current project
    if (groupBy === "tasks" && id) {
      fetchTaskList(id);
    }

    // If Group By Issues, fetch issues for the current project
    if (groupBy === "issues" && id) {
      fetchIssueList(id);
    }

    // If Group By Activities, fetch activities for the current project
    if (groupBy === "activities" && id) {
      fetchActivityList(id);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setIsAddingToGroup(false);
    form.resetFields();
    setSelectedProjectForPhase(null);
  };

  const handleOk = () => {
    // First check if form is properly initialized
    if (!form) {
      console.error("Form is not initialized");
      return;
    }

    try {
      form
        .validateFields()
        .then((values) => {
          submitDocument(values);
        })
        .catch((errorInfo) => {
          console.error("Form validation failed:", errorInfo);
          // The form will automatically show validation errors
          // No need to show additional error message as Ant Design handles this
        });
    } catch (error) {
      console.error("Unexpected error during form validation:", error);
      // Show a user-friendly error message using SweetAlert
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An unexpected error occurred. Please try again.",
        confirmButtonText: "Ok",
      });
    }
  };

  const handleLinkTypeChange = (value: string, index: number) => {
    // Reset Link To when Link Type changes
    form.setFieldsValue({
      documents: form
        .getFieldsValue()
        .documents.map((doc: any, i: number) =>
          i === index ? { ...doc, linkTo: undefined } : doc
        ),
    });

    // Reset selected project for phase when link type changes
    if (value !== "phase") {
      setSelectedProjectForPhase(null);
    }
  };

  const handleLinkToChange = (value: string, index: number) => {
    // Get the current link type from form
    const currentLinkType = form.getFieldValue([
      "documents",
      index,
      "linkType",
    ]);

    // If phase is selected as link type, fetch phases for the selected project
    if (currentLinkType === "phase") {
      const selectedProjectId = parseInt(value);

      // Set the selected project for phase
      setSelectedProjectForPhase(selectedProjectId);

      // Clear the phase field when project changes
      form.setFieldsValue({
        documents: form
          .getFieldsValue()
          .documents.map((doc: any, i: number) =>
            i === index ? { ...doc, phase: undefined } : doc
          ),
      });

      // Fetch phases for the selected project
      fetchPhaseList(selectedProjectId);
    }
  };

  const fetchData = async (params: any, append = false) => {
    setLoading(true);
    try {
      const {
        pageSize = 10,
        searchQuery = "",
        sortOrder = "DESC",
        direction = "P",
        pageDiff = 0,
        paginationId = 10,
      } = params;

      // Map groupBy to correct modules parameter
      const getModulesByGroup = (group: string) => {
        switch (group) {
          case "projects":
            return "project";
          case "phases":
            return "phase";
          case "activities":
            return "activity";
          case "tasks":
            return "task";
          case "issues":
            return "issue";
          default:
            return "project";
        }
      };

      // Build request payload with ID fields based on groupBy
      const requestPayload: any = {
        modules: getModulesByGroup(groupBy),
        sortOrder,
        direction,
        pageSize,
        pageDiff,
        paginationId,
        searchQuery: searchQuery || searchQuery, // Use the searchQuery state
      };

      // Add ID fields based on groupBy
      if (groupBy === "projects") {
        // For projects, pass projectIds if id is available
        if (id) {
          requestPayload.projectIds = id.toString();
        }
      } else if (groupBy === "phases") {
        // For phases, pass phaseIds from phasesList
        if (phasesList.length > 0) {
          const phaseIds = phasesList
            .map((phase: any) => phase.phaseId.toString())
            .join(",");
          requestPayload.phaseIds = phaseIds;
        }
      } else if (groupBy === "activities") {
        // For activities, pass activityIds from activityList
        if (activityList.length > 0) {
          const activityIds = activityList
            .map((activity: any) => activity.activityId.toString())
            .join(",");
          requestPayload.activityIds = activityIds;
        }
      } else if (groupBy === "tasks") {
        // For tasks, pass taskIds from taskList
        if (taskList.length > 0) {
          const taskIds = taskList
            .map((task: any) => task.taskId.toString())
            .join(",");
          requestPayload.taskIds = taskIds;
        }
      } else if (groupBy === "issues") {
        // For issues, pass issueIds from issueList
        if (issueList.length > 0) {
          const issueIds = issueList
            .map((issue: any) => issue.issueId.toString())
            .join(",");
          requestPayload.issueIds = issueIds;
        }
      }

      const apiResponse: any = await documentApis.listDocuments(requestPayload);

      // Check if response has data directly or nested
      let responseData = apiResponse;
      let responseStatus = true; // Default to true

      if (apiResponse && typeof apiResponse === "object") {
        if (apiResponse.data !== undefined) {
          responseData = apiResponse.data;
          responseStatus = apiResponse.status;
        } else if (apiResponse.list !== undefined) {
          // Response is already in the correct format
          responseData = apiResponse;
          responseStatus = true;
        }
      }

      if (responseStatus && responseData) {
        const { list, count } = responseData || {};

        if (list && Array.isArray(list)) {
          setPagination((prev) => ({
            ...prev,
            total: Number(count) || 0,
          }));

          // Group documents by linkType and linkTo if needed
          const groupedData = groupDocumentsByLink(list);

          // Add key to each item for Ant Design table
          const dataWithKeys = groupedData.map((item: any, index: number) => ({
            ...item,
            key: item.docId || index,
            // Ensure customDocName is properly set for display
            customDocName: item.customDocName || item.docName || item.docNames,
          }));

          setDataSource(dataWithKeys);
        } else {
          setDataSource([]);
        }
      } else {
        setDataSource([]);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };
  const [projectList, setProjectList] = useState<any[]>([]);
  const [phaseList, setPhaseList] = useState<any[]>([]);
  const [selectedProjectForPhase, setSelectedProjectForPhase] = useState<
    number | null
  >(null);
  const [projectNameMapping, setProjectNameMapping] = useState<{
    [key: string]: string;
  }>({});
  const [phaseNameMapping, setPhaseNameMapping] = useState<{
    [key: string]: string;
  }>({});
  const [taskList, setTaskList] = useState<any[]>([]);
  const [taskNameMapping, setTaskNameMapping] = useState<{
    [key: string]: string;
  }>({});
  const [issueList, setIssueList] = useState<any[]>([]);
  const [issueNameMapping, setIssueNameMapping] = useState<{
    [key: string]: string;
  }>({});
  const [activityList, setActivityList] = useState<any[]>([]);
  const [activityNameMapping, setActivityNameMapping] = useState<{
    [key: string]: string;
  }>({});

  // Add state for phases list
  const [phasesList, setPhasesList] = useState<any[]>([]);

  // Function to fetch phases for current project
  const fetchPhasesForProject = async () => {
    if (groupBy === "phases" && id) {
      try {
        const requestPayload = {
          projectId: id,
        };

        const apiResponse: any = await documentApis.phaseList(requestPayload);

        let responseData = apiResponse;
        let responseStatus = true;

        if (apiResponse && typeof apiResponse === "object") {
          if (apiResponse.data !== undefined) {
            responseData = apiResponse.data;
            responseStatus = apiResponse.status;
          } else if (Array.isArray(apiResponse)) {
            responseData = apiResponse;
            responseStatus = true;
          }
        }

        if (responseStatus && responseData && Array.isArray(responseData)) {
          setPhasesList(responseData);

          // Also create mapping for phase IDs to names (for form submission)
          const phaseMapping: { [key: string]: string } = {};
          responseData.forEach((phase: any) => {
            phaseMapping[phase.phaseId.toString()] = phase.phaseName;
          });
          setPhaseNameMapping(phaseMapping);
        } else {
          setPhasesList([]);
          setPhaseNameMapping({});
        }
      } catch (error) {
        console.error("Error loading phases for project:", error);
        setPhasesList([]);
      }
    }
  };

  const fetchProjectList = async () => {
    try {
      const requestPayload = {
        projectStatus: derivedProjectStatus,
      };

      const apiResponse: any = await documentApis.projectList(requestPayload);

      // Check if response has data directly or nested
      let responseData = apiResponse;
      let responseStatus = true;

      if (apiResponse && typeof apiResponse === "object") {
        if (apiResponse.data !== undefined) {
          responseData = apiResponse.data;
          responseStatus = apiResponse.status;
        } else if (Array.isArray(apiResponse)) {
          // Response is already an array
          responseData = apiResponse;
          responseStatus = true;
        }
      }

      if (responseStatus && responseData && Array.isArray(responseData)) {
        setProjectList(responseData);

        // Create mapping for project IDs to names
        const projectMapping: { [key: string]: string } = {};
        responseData.forEach((project: any) => {
          projectMapping[project.projectId.toString()] = project.projectTitle;
        });
        setProjectNameMapping(projectMapping);
      } else {
        setProjectList([]);
        setProjectNameMapping({});
      }
    } catch (error) {
      console.error("Error loading project list:", error);
      setProjectList([]);
    }
  };
  const fetchPhaseList = async (projectId: number) => {
    try {
      const requestPayload = {
        projectId: projectId,
      };

      const apiResponse: any = await documentApis.phaseList(requestPayload);

      // Check if response has data directly or nested
      let responseData = apiResponse;
      let responseStatus = true;

      if (apiResponse && typeof apiResponse === "object") {
        if (apiResponse.data !== undefined) {
          responseData = apiResponse.data;
          responseStatus = apiResponse.status;
        } else if (Array.isArray(apiResponse)) {
          // Response is already an array
          responseData = apiResponse;
          responseStatus = true;
        }
      }

      if (responseStatus && responseData && Array.isArray(responseData)) {
        setPhaseList(responseData);

        // Create mapping for phase IDs to names
        const phaseMapping: { [key: string]: string } = {};
        responseData.forEach((phase: any) => {
          phaseMapping[phase.phaseId.toString()] = phase.phaseName;
        });
        setPhaseNameMapping(phaseMapping);

        if (responseData.length === 0) {
          // No phases found for this project
        }
      } else {
        setPhaseList([]);
        setPhaseNameMapping({});
      }
    } catch (error) {
      console.error("Error loading phase list:", error);
      setPhaseList([]);
    }
  };
  const fetchTaskList = async (projectId: number) => {
    try {
      const requestPayload = {
        projectId: projectId,
        derivedProjectStatus: derivedProjectStatus,
      };

      const apiResponse: any = await tasksApis.fetchTaskDropDown(
        requestPayload
      );

      // Check if response has data directly or nested
      let responseData = apiResponse;
      let responseStatus = true;

      if (apiResponse && typeof apiResponse === "object") {
        if (apiResponse.data !== undefined) {
          responseData = apiResponse.data;
          responseStatus = apiResponse.status;
        } else if (Array.isArray(apiResponse)) {
          // Response is already an array
          responseData = apiResponse;
          responseStatus = true;
        }
      }

      if (responseStatus && responseData && Array.isArray(responseData)) {
        setTaskList(responseData);

        // Create mapping for task IDs to names
        const taskMapping: { [key: string]: string } = {};
        responseData.forEach((task: any) => {
          taskMapping[task.taskId.toString()] = task.taskName;
        });
        setTaskNameMapping(taskMapping);

        if (responseData.length === 0) {
          // No tasks found for this project
        }
      } else {
        setTaskList([]);
        setTaskNameMapping({});
      }
    } catch (error) {
      console.error("Error loading task list:", error);
      setTaskList([]);
    }
  };
  const fetchIssueList = async (projectId: number) => {
    try {
      const apiResponse: any = await issueApis.fetchIssueDropDown(projectId);

      // Check if response has data directly or nested
      let responseData = apiResponse;
      let responseStatus = true;

      if (apiResponse && typeof apiResponse === "object") {
        if (apiResponse.data !== undefined) {
          responseData = apiResponse.data;
          responseStatus = apiResponse.status;
        } else if (Array.isArray(apiResponse)) {
          // Response is already an array
          responseData = apiResponse;
          responseStatus = true;
        }
      }

      if (responseStatus && responseData && Array.isArray(responseData)) {
        setIssueList(responseData);

        // Create mapping for issue IDs to names
        const issueMapping: { [key: string]: string } = {};
        responseData.forEach((issue: any) => {
          issueMapping[issue.issueId.toString()] = issue.issueName;
        });
        setIssueNameMapping(issueMapping);

        if (responseData.length === 0) {
          // No issues found for this project
        }
      } else {
        setIssueList([]);
        setIssueNameMapping({});
      }
    } catch (error) {
      console.error("Error loading issue list:", error);
      setIssueList([]);
    }
  };
  const fetchActivityList = async (projectId: number) => {
    try {
      const requestParams = {
        projectId: projectId,
      };

      const apiResponse: any = await documentApis.fetchActivityDropDown(
        requestParams
      );

      // Check if response has data directly or nested
      let responseData = apiResponse;
      let responseStatus = true;

      if (apiResponse && typeof apiResponse === "object") {
        if (apiResponse.data !== undefined) {
          responseData = apiResponse.data;
          responseStatus = apiResponse.status;
        } else if (Array.isArray(apiResponse)) {
          // Response is already an array
          responseData = apiResponse;
          responseStatus = true;
        }
      }

      if (responseStatus && responseData && Array.isArray(responseData)) {
        setActivityList(responseData);

        // Create mapping for activity IDs to names
        const activityMapping: { [key: string]: string } = {};
        responseData.forEach((activity: any) => {
          activityMapping[activity.activityId.toString()] =
            activity.activityName;
        });
        setActivityNameMapping(activityMapping);

        if (responseData.length === 0) {
          // No activities found for this project
        }
      } else {
        setActivityList([]);
        setActivityNameMapping({});
      }
    } catch (error) {
      console.error("Error loading activity list:", error);
      setActivityList([]);
    }
  };
  const submitDocument = async (formData: any) => {
    try {
      setSaveLoading(true);

      // Extract documents array from form data
      const { documents } = formData;

      if (!documents || documents.length === 0) {
        console.error("No documents to upload");
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No documents to upload",
          confirmButtonText: "Ok",
        });
        return;
      }

      // Validate each document has required fields
      for (let i = 0; i < documents.length; i++) {
        const doc = documents[i];
        if (!doc.linkType) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: `Document ${i + 1}: Please select Link Type`,
            confirmButtonText: "Ok",
          });
          return;
        }
        if (!doc.linkTo) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: `Document ${i + 1}: Please select Link To`,
            confirmButtonText: "Ok",
          });
          return;
        }
        if (!doc.docName || doc.docName.trim() === "") {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: `Document ${i + 1}: Please enter Document Name`,
            confirmButtonText: "Ok",
          });
          return;
        }
        if (!doc.file || !doc.file.fileList || doc.file.fileList.length === 0) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: `Document ${i + 1}: Please select at least one file`,
            confirmButtonText: "Ok",
          });
          return;
        }
      }

      // Check if any files are selected
      const hasFiles = documents.some(
        (doc: any) =>
          doc.file && doc.file.fileList && doc.file.fileList.length > 0
      );

      if (!hasFiles) {
        console.error("No files selected for upload");
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Please select at least one file to upload",
          confirmButtonText: "Ok",
        });
        return;
      }

      // Create FormData to handle file uploads
      const formDataToSend = new FormData();

      // Prepare the payload according to the API structure
      const newFileData: any[] = [];
      let fileIndex = 0; // Global file index for all files across all documents

      for (const doc of documents) {
        let linkTo = doc.linkTo;
        let phaseId: number | null = null;
        let projectId: number | null = null;
        let activityId: number | null = null;
        let taskId: number | null = null;
        let issueId: number | null = null;

        // Convert IDs to names for API payload
        if (doc.linkType === "project") {
          // Check if doc.linkTo is a project ID or project name
          // First, try to get from mapping (if it's an ID)
          let foundProjectId: number | null = null;
          if (projectNameMapping[doc.linkTo]) {
            // doc.linkTo is an ID, get the name from mapping
            linkTo = projectNameMapping[doc.linkTo];
            foundProjectId = parseInt(doc.linkTo);
          } else {
            // doc.linkTo might be a name, try to find the ID from projectList
            const foundProject = projectList.find(
              (p: any) => p.projectTitle === doc.linkTo
            );
            if (foundProject) {
              linkTo = foundProject.projectTitle;
              foundProjectId = foundProject.projectId;
            } else {
              // Fallback: try to parse as ID
              const parsedId = parseInt(doc.linkTo);
              if (!isNaN(parsedId)) {
                foundProjectId = parsedId;
                linkTo = projectNameMapping[doc.linkTo] || doc.linkTo;
              } else {
                // If all else fails, use doc.linkTo as is
                linkTo = doc.linkTo;
              }
            }
          }
          // Fallback: if we still don't have a projectId and groupBy is projects, use id from location.state
          if (!foundProjectId && groupBy === "projects" && id) {
            foundProjectId = id;
            linkTo = projectName || doc.linkTo;
          }
          projectId = foundProjectId;
        } else if (doc.linkType === "phase") {
          // Get phase name from mapping
          if (groupBy === "phases") {
            // When groupBy is phases, use linkTo directly (it's the phase ID)
            // Try to get phase name from mapping first
            let phaseName = phaseNameMapping[doc.linkTo];

            // If mapping is not available, try to get from phasesList (for groupBy phases)
            if (!phaseName && phasesList.length > 0) {
              const phase = phasesList.find(
                (p: any) => p.phaseId.toString() === doc.linkTo
              );
              phaseName = phase ? phase.phaseName : doc.linkTo;
            }

            // If still not found, try to get from phaseList
            if (!phaseName && phaseList.length > 0) {
              const phase = phaseList.find(
                (p: any) => p.phaseId.toString() === doc.linkTo
              );
              phaseName = phase ? phase.phaseName : doc.linkTo;
            }

            // If still not found, try direct API call as last resort
            if (!phaseName || phaseName === doc.linkTo) {
              const apiPhaseName = await getPhaseName(doc.linkTo, id);
              if (apiPhaseName) {
                phaseName = apiPhaseName;
              }
            }

            linkTo = phaseName || doc.linkTo;
            phaseId = parseInt(doc.linkTo);
          } else {
            // When not groupBy phases, use the phase field
            linkTo = phaseNameMapping[doc.phase] || doc.phase;
            phaseId = parseInt(doc.phase);
          }
        } else if (doc.linkType === "task") {
          // Get task name from mapping
          if (groupBy === "tasks") {
            // When groupBy is tasks, use linkTo directly (it's the task ID)
            // Try to get task name from mapping first
            let taskName = taskNameMapping[doc.linkTo];

            // If mapping is not available, try to get from taskList
            if (!taskName && taskList.length > 0) {
              const task = taskList.find(
                (t: any) => t.taskId.toString() === doc.linkTo
              );
              taskName = task ? task.taskName : doc.linkTo;
            }

            linkTo = taskName || doc.linkTo;
            taskId = parseInt(doc.linkTo);
          } else {
            // When not groupBy tasks, use mapping
            linkTo = taskNameMapping[doc.linkTo] || doc.linkTo;
            taskId = parseInt(doc.linkTo);
          }
        } else if (doc.linkType === "issue") {
          // Get issue name from mapping
          if (groupBy === "issues") {
            // When groupBy is issues, use linkTo directly (it's the issue ID)
            // Try to get issue name from mapping first
            let issueName = issueNameMapping[doc.linkTo];

            // If mapping is not available, try to get from issueList
            if (!issueName && issueList.length > 0) {
              const issue = issueList.find(
                (i: any) => i.issueId.toString() === doc.linkTo
              );
              issueName = issue ? issue.issueName : doc.linkTo;
            }

            linkTo = issueName || doc.linkTo;
            issueId = parseInt(doc.linkTo);
          } else {
            // When not groupBy issues, use mapping
            linkTo = issueNameMapping[doc.linkTo] || doc.linkTo;
            issueId = parseInt(doc.linkTo);
          }
        } else if (doc.linkType === "activity") {
          // Get activity name from mapping
          if (groupBy === "activities") {
            // When groupBy is activities, use linkTo directly (it's the activity ID)
            // Try to get activity name from mapping first
            let activityName = activityNameMapping[doc.linkTo];

            // If mapping is not available, try to get from activityList
            if (!activityName && activityList.length > 0) {
              const activity = activityList.find(
                (a: any) => a.activityId.toString() === doc.linkTo
              );
              activityName = activity ? activity.activityName : doc.linkTo;
            }

            linkTo = activityName || doc.linkTo;
            activityId = parseInt(doc.linkTo);
          } else {
            // When not groupBy activities, use mapping
            linkTo = activityNameMapping[doc.linkTo] || doc.linkTo;
            activityId = parseInt(doc.linkTo);
          }
        }

        // Add files to FormData and create payload items for each file
        if (doc.file && doc.file.fileList) {
          doc.file.fileList.forEach((fileItem: any) => {
            try {
              if (fileItem.originFileObj) {
                // Check file size (10MB limit)
                if (fileItem.size > 10 * 1024 * 1024) {
                  Swal.fire({
                    icon: "error",
                    title: "File Size Error",
                    text: `File ${fileItem.name} is too large. Maximum size is 10MB.`,
                    confirmButtonText: "Ok",
                  });
                  return;
                }

                formDataToSend.append(
                  `files`,
                  fileItem.originFileObj,
                  fileItem.name
                );

                // Create a payload item for each file
                const payloadItem: any = {
                  indexOfFileLocated: fileIndex,
                  linkType: doc.linkType,
                  linkTo: linkTo,
                  docName: doc.docName || fileItem.name, // Use file name if doc name is empty
                  remarks: doc.remarks || "",
                  action: "Create",
                };

                // Add appropriate ID based on link type
                if (doc.linkType === "project" && projectId) {
                  payloadItem.projectId = projectId;
                } else if (doc.linkType === "phase" && phaseId) {
                  payloadItem.phaseId = phaseId;
                } else if (doc.linkType === "activity" && activityId) {
                  payloadItem.activityId = activityId;
                } else if (doc.linkType === "task" && taskId) {
                  payloadItem.taskId = taskId;
                } else if (doc.linkType === "issue" && issueId) {
                  payloadItem.issueId = issueId;
                }

                newFileData.push(payloadItem);
                fileIndex++; // Increment global file index
              }
            } catch (fileError) {
              console.error(
                `Error processing file ${fileItem?.name}:`,
                fileError
              );
              Swal.fire({
                icon: "error",
                title: "File Processing Error",
                text: `Error processing file ${
                  fileItem?.name || "unknown"
                }. Please try again.`,
                confirmButtonText: "Ok",
              });
              return;
            }
          });
        }
      }

      // Add the metadata to FormData with correct key names
      // Create the filesData object as expected by the API
      const filesData = {
        newfiledata: newFileData,
        deletedFileData: [],
      };

      // Send filesData as a JSON string
      formDataToSend.append("filesData", JSON.stringify(filesData));

      // Call the document upload API with FormData
      let response;
      try {
        response = await documentApis.uploadDocument(formDataToSend);
      } catch (apiError: any) {
        console.error("API call failed:", apiError);
        console.error("API error details:", {
          message: apiError?.message || "Unknown error",
          response: apiError?.response || "No response",
          status: apiError?.response?.status || "No status",
          data: apiError?.response?.data || "No data",
        });

        // Extract the actual error message from the API response
        let errorMessage = "API call failed. Please try again.";

        if (apiError?.response?.data) {
          // Try to get the message from response.data
          errorMessage =
            apiError.response.data.message ||
            apiError.response.data.error ||
            errorMessage;
        } else if (apiError?.message) {
          // Fallback to apiError.message
          errorMessage = apiError.message;
        }

        Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: errorMessage,
          confirmButtonText: "Ok",
        });
        return;
      }

      if (response && response.status) {
        // Success - close drawer and refresh data
        setIsModalVisible(false);
        setIsAddingToGroup(false);
        form.resetFields();

        setSelectedProjectForPhase(null);

        // Refresh the documents list
        fetchData({
          current: 1,
          pageSize: pagination.pageSize,
          sortOrder: "DESC",
          direction: "P",
          pageDiff: 0,
          paginationId: 10,
        });

        // Show success message using SweetAlert
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Documents uploaded successfully!",
          confirmButtonText: "Ok",
        });
      } else {
        console.error("Document upload failed:", response);
        console.error("Response details:", {
          status: response?.status,
          statusCode: response?.statusCode,
          error: response?.error,
          message: response?.message,
        });

        // Extract the actual error message from the response
        let errorMessage = "Document upload failed. Please try again.";

        if (response?.message) {
          errorMessage = response.message;
        } else if (response?.error) {
          errorMessage = response.error;
        }

        Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: errorMessage,
          confirmButtonText: "Ok",
        });
      }
    } catch (error) {
      console.error("Document upload error:", error);

      // Show more specific error message
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      Swal.fire({
        icon: "error",
        title: "Upload Error",
        text: `Document upload error: ${errorMessage}. Please check all required fields and try again.`,
        confirmButtonText: "Ok",
      });
    } finally {
      setSaveLoading(false);
    }
  };

  // Delete document function
  const handleDeleteDocument = (record: any) => {
    // Check if this is a grouped record with multiple documents
    const isGrouped = record.documents && record.documents.length > 1;
    const fileCount = isGrouped ? record.documents.length : 1;

    Swal.fire({
      title: "Delete Entire Document Record?",
      text: isGrouped
        ? `This will delete the entire document record with ${fileCount} files. This action cannot be undone.`
        : "This will delete the entire document record. This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete entire record!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setSaveLoading(true);

          // Create FormData for deletion
          const formDataToSend = new FormData();

          // Create payload for deletion
          const filesData = {
            newfiledata: [],
            deletedFileData: [
              {
                docId: record.docId,
              },
            ],
          };

          formDataToSend.append("filesData", JSON.stringify(filesData));

          let response;
          try {
            response = await documentApis.uploadDocument(formDataToSend);
          } catch (apiError: any) {
            console.error("Delete API call failed:", apiError);

            // Extract the actual error message from the API response
            let errorMessage = "Delete API call failed. Please try again.";

            if (apiError?.response?.data) {
              // Try to get the message from response.data
              errorMessage =
                apiError.response.data.message ||
                apiError.response.data.error ||
                errorMessage;
            } else if (apiError?.message) {
              // Fallback to apiError.message
              errorMessage = apiError.message;
            }

            Swal.fire({
              icon: "error",
              title: "Delete Failed",
              text: errorMessage,
              confirmButtonText: "Ok",
            });
            return;
          }

          if (response && response.status) {
            Swal.fire(
              "Deleted!",
              `Entire document record with ${fileCount} file(s) has been deleted.`,
              "success"
            );

            // Refresh the documents list
            fetchData({
              current: 1,
              pageSize: pagination.pageSize,
              sortOrder: "DESC",
              direction: "P",
              pageDiff: 0,
              paginationId: 10,
            });
          } else {
            console.error("Document delete failed:", response);

            // Extract the actual error message from the response
            let errorMessage = "Document delete failed. Please try again.";

            if (response?.message) {
              errorMessage = response.message;
            } else if (response?.error) {
              errorMessage = response.error;
            }

            Swal.fire({
              icon: "error",
              title: "Delete Failed",
              text: errorMessage,
              confirmButtonText: "Ok",
            });
          }
        } catch (error) {
          console.error("Document delete error:", error);
          Swal.fire("Error", "Failed to delete document.", "error");
        } finally {
          setSaveLoading(false);
        }
      }
    });
  };

  // Delete all files function (for grouped documents)
  const handleDeleteAllFiles = (record: any) => {
    const fileCount = record.documents.length;

    Swal.fire({
      title: "Delete All Files?",
      text: `This will delete all ${fileCount} files in this document record. This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete all files!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setSaveLoading(true);

          // Create FormData for deletion
          const formDataToSend = new FormData();

          // Create payload for deletion - delete all files by sending all file indices
          const deletedFileData = record.documents.map(
            (doc: any, index: number) => ({
              docId: doc.docId,
              fileIndex: index,
            })
          );

          const filesData = {
            newfiledata: [],
            deletedFileData: deletedFileData,
          };

          formDataToSend.append("filesData", JSON.stringify(filesData));

          let response;
          try {
            response = await documentApis.uploadDocument(formDataToSend);
          } catch (apiError: any) {
            console.error("Delete All Files API call failed:", apiError);

            // Extract the actual error message from the API response
            let errorMessage =
              "Delete All Files API call failed. Please try again.";

            if (apiError?.response?.data) {
              // Try to get the message from response.data
              errorMessage =
                apiError.response.data.message ||
                apiError.response.data.error ||
                errorMessage;
            } else if (apiError?.message) {
              // Fallback to apiError.message
              errorMessage = apiError.message;
            }

            Swal.fire({
              icon: "error",
              title: "Delete Failed",
              text: errorMessage,
              confirmButtonText: "Ok",
            });
            return;
          }

          if (response && response.status) {
            Swal.fire(
              "Deleted!",
              `All ${fileCount} files have been deleted successfully.`,
              "success"
            );

            // Refresh the documents list
            fetchData({
              current: 1,
              pageSize: pagination.pageSize,
              sortOrder: "DESC",
              direction: "P",
              pageDiff: 0,
              paginationId: 10,
            });
          } else {
            console.error("Delete All Files failed:", response);

            // Extract the actual error message from the response
            let errorMessage = "Delete All Files failed. Please try again.";

            if (response?.message) {
              errorMessage = response.message;
            } else if (response?.error) {
              errorMessage = response.error;
            }

            Swal.fire({
              icon: "error",
              title: "Delete Failed",
              text: errorMessage,
              confirmButtonText: "Ok",
            });
          }
        } catch (error) {
          console.error("Delete All Files error:", error);
          Swal.fire("Error", "Failed to delete all files.", "error");
        } finally {
          setSaveLoading(false);
        }
      }
    });
  };

  // Table change handler
  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams: any = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize !== pagination.pageSize) {
      // if page size is changed
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1; // calculating new current page based on new page size
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current !== pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current
            ? "F" // Forward
            : "B"; // Backward
        const lastItemId = dataSource[dataSource.length - 1]?.paginationId;
        const firstItemId = dataSource[0]?.paginationId;
        apiParams.paginationId =
          apiParams.direction === "F"
            ? lastItemId || null // Last item ID for backward direction
            : firstItemId || null; // First item ID for forward direction

        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: apiParams.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData({
      ...apiParams,
    });
  };

  // Delete individual file function
  const handleDeleteFile = (document: any, fileIndex: number) => {
    const fileName = document.docName || document.customDocName || "this file";

    Swal.fire({
      title: "Delete File?",
      text: `Do you want to delete "${fileName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete this file!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setSaveLoading(true);

          // Create FormData for file deletion
          const formDataToSend = new FormData();

          // Create payload for file deletion
          const filesData = {
            newfiledata: [],
            deletedFileData: [
              {
                docId: document.docId,
                fileIndex: fileIndex,
              },
            ],
          };

          formDataToSend.append("filesData", JSON.stringify(filesData));

          let response;
          try {
            response = await documentApis.uploadDocument(formDataToSend);
          } catch (apiError: any) {
            console.error("File delete API call failed:", apiError);

            // Extract the actual error message from the API response
            let errorMessage = "File delete API call failed. Please try again.";

            if (apiError?.response?.data) {
              // Try to get the message from response.data
              errorMessage =
                apiError.response.data.message ||
                apiError.response.data.error ||
                errorMessage;
            } else if (apiError?.message) {
              // Fallback to apiError.message
              errorMessage = apiError.message;
            }

            Swal.fire({
              icon: "error",
              title: "Delete Failed",
              text: errorMessage,
              confirmButtonText: "Ok",
            });
            return;
          }

          if (response && response.status) {
            Swal.fire(
              "Deleted!",
              `File "${fileName}" has been deleted.`,
              "success"
            );

            // Refresh the documents list
            fetchData({
              current: 1,
              pageSize: pagination.pageSize,
              sortOrder: "DESC",
              direction: "P",
              pageDiff: 0,
              paginationId: 10,
            });
          } else {
            console.error("File delete failed:", response);

            // Extract the actual error message from the response
            let errorMessage = "File delete failed. Please try again.";

            if (response?.message) {
              errorMessage = response.message;
            } else if (response?.error) {
              errorMessage = response.error;
            }

            Swal.fire({
              icon: "error",
              title: "Delete Failed",
              text: errorMessage,
              confirmButtonText: "Ok",
            });
          }
        } catch (error) {
          console.error("File delete error:", error);
          Swal.fire("Error", "Failed to delete file.", "error");
        } finally {
          setSaveLoading(false);
        }
      }
    });
  };

  // Fetch project list on component mount
  useEffect(() => {
    if (derivedProjectStatus) {
      fetchProjectList();
    }
  }, [derivedProjectStatus]);

  // Consolidated data fetching logic
  useEffect(() => {
    // Skip if this is the same groupBy as before (prevents duplicate calls)
    if (currentGroupByRef.current === groupBy && !isInitialLoadRef.current) {
      return;
    }

    currentGroupByRef.current = groupBy;
    isInitialLoadRef.current = false;

    // Fetch data based on groupBy
    if (groupBy === "projects") {
      // For projects, fetch data immediately
      fetchData({
        current: 1,
        pageSize: pagination.pageSize,
        sortOrder: "DESC",
        direction: "P",
        pageDiff: 0,
        paginationId: 10,
      });
    } else if (groupBy === "phases" && id) {
      // For phases, fetch phases first, then data
      fetchPhasesForProject();
    } else if (groupBy === "activities" && id) {
      // For activities, fetch activities first, then data
      fetchActivityList(id);
    } else if (groupBy === "tasks" && id) {
      // For tasks, fetch tasks first, then data
      fetchTaskList(id);
    } else if (groupBy === "issues" && id) {
      // For issues, fetch issues first, then data
      fetchIssueList(id);
    }
  }, [groupBy, id]);

  // Fetch data after lists are loaded (only for non-project types)
  useEffect(() => {
    if (
      groupBy === "phases" &&
      phasesList.length > 0 &&
      currentGroupByRef.current === "phases"
    ) {
      fetchData({
        current: 1,
        pageSize: pagination.pageSize,
        sortOrder: "DESC",
        direction: "P",
        pageDiff: 0,
        paginationId: 10,
      });
    }
  }, [phasesList, groupBy]);

  useEffect(() => {
    if (
      groupBy === "activities" &&
      activityList.length > 0 &&
      currentGroupByRef.current === "activities"
    ) {
      fetchData({
        current: 1,
        pageSize: pagination.pageSize,
        sortOrder: "DESC",
        direction: "P",
        pageDiff: 0,
        paginationId: 10,
      });
    }
  }, [activityList, groupBy]);

  useEffect(() => {
    if (
      groupBy === "tasks" &&
      taskList.length > 0 &&
      currentGroupByRef.current === "tasks"
    ) {
      fetchData({
        current: 1,
        pageSize: pagination.pageSize,
        sortOrder: "DESC",
        direction: "P",
        pageDiff: 0,
        paginationId: 10,
      });
    }
  }, [taskList, groupBy]);

  useEffect(() => {
    if (
      groupBy === "issues" &&
      issueList.length > 0 &&
      currentGroupByRef.current === "issues"
    ) {
      fetchData({
        current: 1,
        pageSize: pagination.pageSize,
        sortOrder: "DESC",
        direction: "P",
        pageDiff: 0,
        paginationId: 10,
      });
    }
  }, [issueList, groupBy]);

  // Table columns configuration
  const columns = [
    // {
    //   title: "Document Id",
    //   dataIndex: "docId",
    //   key: "docId",
    //   width: 80,
    //   fixed: "left" as const,
    // },
    {
      title: "Document Name",
      dataIndex: "customDocName",
      key: "customDocName",
      width: 180,
      ellipsis: true,
      render: (text: string, record: any) => {
        // If this is a grouped record, show the grouped information
        if (record.docCount && record.docCount > 1) {
          const tooltipText = record.documents
            ?.map((doc: any) => doc.docName)
            .join(", ");
          return <div title={tooltipText}>{record.docNames}</div>;
        }
        // Otherwise show the single document name
        return <span title={record.docName || text}>{text}</span>;
      },
    },
    {
      title: "Link Type",
      dataIndex: "linkType",
      key: "linkType",
      width: 100,
      render: (text: string) => (
        <span style={{ textTransform: "capitalize" }}>{text}</span>
      ),
    },
    {
      title: "Link To",
      dataIndex: "linkTo",
      key: "linkTo",
      width: 150,
      ellipsis: true,
      render: (text: string, record: any) => {
        const displayName = getLinkToDisplayName(record.linkType, text);
        return (
          <span>{displayName && displayName.trim() ? displayName : "N/A"}</span>
        );
      },
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 200,
      ellipsis: true,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Actions",
      key: "filePath",
      width: 180,
      fixed: "right" as const,
      render: (_: any, record: any) => {
        // If this is a grouped record, show actions for all documents
        if (record.documents && record.documents.length > 1) {
          return (
            <Space size="small">
              <Dropdown
                menu={{
                  items: record.documents.map((doc: any, index: number) => ({
                    key: index,
                    label: (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Icon name="eye" style={{ color: "#4169E1" }} />
                        <span>{doc.docName}</span>
                      </div>
                    ),
                    onClick: () => {
                      // For view, open in new tab
                      window.open(doc.filePath, "_blank");
                    },
                  })),
                }}
                placement="bottomRight"
                trigger={["hover"]}
              >
                <span
                  style={{
                    cursor: "pointer",
                    color: "#4169E1",
                    fontSize: "18px",
                    display: "flex",
                    alignItems: "center",
                    marginRight: "8px",
                  }}
                  title={`View ${record.documents.length} Documents`}
                >
                  <Icon name="eye" />
                </span>
              </Dropdown>

              <Dropdown
                menu={{
                  items: [
                    // Option to download all files at once
                    {
                      key: "download-all",
                      label: (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            color: "#52c41a",
                            fontWeight: "500",
                          }}
                        >
                          <Icon name="download" style={{ color: "#52c41a" }} />
                          <span>
                            Download All Files ({record.documents.length} files)
                          </span>
                        </div>
                      ),
                      onClick: () => {
                        // Download all files
                        record.documents.forEach((doc: any) => {
                          downloadDocument(doc.filePath, doc.docName);
                        });
                      },
                    },
                    // Divider
                    {
                      type: "divider",
                    },
                    // Individual file download options
                    ...record.documents.map((doc: any, index: number) => ({
                      key: index,
                      label: (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            padding: "4px 8px",
                            cursor: "pointer",
                            position: "relative",
                            minHeight: "24px",
                          }}
                          className="file-row-hover"
                        >
                          <span
                            style={{ fontSize: "12px", color: "#666", flex: 1 }}
                          >
                            {doc.docName}
                          </span>
                          <Icon
                            name="download"
                            style={{
                              cursor: "pointer",
                              fontSize: "14px",
                              marginLeft: "8px",
                            }}
                            className="download-icon"
                            onClick={(e: React.MouseEvent<HTMLElement>) => {
                              e.stopPropagation();
                              downloadDocument(doc.filePath, doc.docName);
                            }}
                          />
                        </div>
                      ),
                      onClick: () => {
                        // For download, trigger file download
                        downloadDocument(doc.filePath, doc.docName);
                      },
                    })),
                  ],
                }}
                placement="bottomRight"
                trigger={["hover"]}
              >
                <span
                  style={{
                    cursor: "pointer",
                    color: "#52c41a",
                    fontSize: "18px",
                    display: "flex",
                    alignItems: "center",
                    marginRight: "8px",
                  }}
                  title={`Download ${record.documents.length} Documents`}
                >
                  <Icon name="download" />
                </span>
              </Dropdown>

              {/* Add More Documents Button for Grouped Documents */}
              <span
                style={{
                  cursor: "pointer",
                  color: "#4169E1",
                  fontSize: "18px",
                  display: "flex",
                  alignItems: "center",
                  marginRight: "8px",
                }}
                title="Add More Documents to This Group"
                onClick={() => {
                  // Set flag to indicate we're adding to an existing group
                  setIsAddingToGroup(true);
                  // Open the upload modal with pre-filled values in correct format
                  form.setFieldsValue({
                    documents: [
                      {
                        linkType: record.linkType,
                        linkTo: record.linkTo,
                        project:
                          record.linkType === "project"
                            ? record.linkTo
                            : undefined,
                        phase:
                          record.linkType === "phase"
                            ? record.linkTo
                            : undefined,
                        activity:
                          record.linkType === "activity"
                            ? record.linkTo
                            : undefined,
                        task:
                          record.linkType === "task"
                            ? record.linkTo
                            : undefined,
                        issue:
                          record.linkType === "issue"
                            ? record.linkTo
                            : undefined,
                        docName: record.customDocName || record.docNames,
                        remarks: record.remarks || "",
                        file: undefined,
                      },
                    ],
                  });
                  setIsModalVisible(true);
                }}
              >
                <Icon name="plus-circle" />
              </span>

              {groupBy === "projects" && (
                <CancelAccess moduleName="Projects">
                  <Dropdown
                    menu={{
                      items: [
                        // Option to delete entire document record (only show if multiple files)
                        ...(record.documents.length > 1
                          ? [
                              {
                                key: "delete-all",
                                label: (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      color: "#ff4d4f",
                                      fontWeight: "500",
                                    }}
                                  >
                                    <Icon
                                      name="trash"
                                      style={{ color: "#ff4d4f" }}
                                    />
                                    <span>
                                      Delete All Files (
                                      {record.documents.length} files)
                                    </span>
                                  </div>
                                ),
                                onClick: () => handleDeleteAllFiles(record),
                              },
                              // Divider only if multiple files
                              {
                                type: "divider",
                              },
                            ]
                          : []),
                        // Individual file delete options
                        ...record.documents.map((doc: any, index: number) => ({
                          key: `delete-file-${index}`,
                          label: (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                padding: "4px 8px",
                                cursor: "pointer",
                                position: "relative",
                                minHeight: "24px",
                              }}
                              className="file-row-hover"
                            >
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "#666",
                                  flex: 1,
                                }}
                              >
                                {doc.docName}
                              </span>
                              <Icon
                                name="trash"
                                style={{
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  marginLeft: "8px",
                                }}
                                className="delete-icon"
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                  e.stopPropagation();
                                  handleDeleteFile(doc, index);
                                }}
                              />
                            </div>
                          ),
                          onClick: () => {
                            // Delete the individual file
                            handleDeleteFile(doc, index);
                          },
                        })),
                      ],
                    }}
                    placement="bottomRight"
                    trigger={["hover"]}
                  >
                    <span
                      style={{
                        cursor: "pointer",
                        color: "#ff4d4f",
                        fontSize: "18px",
                        display: "flex",
                        alignItems: "center",
                      }}
                      title="Delete Options"
                    >
                      <Icon name="trash" />
                    </span>
                  </Dropdown>
                </CancelAccess>
              )}
              {groupBy === "phases" && (
                <CancelAccess moduleName="Phases">
                  <Dropdown
                    menu={{
                      items: [
                        // Option to delete entire document record (only show if multiple files)
                        ...(record.documents.length > 1
                          ? [
                              {
                                key: "delete-all",
                                label: (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      color: "#ff4d4f",
                                      fontWeight: "500",
                                    }}
                                  >
                                    <Icon
                                      name="trash"
                                      style={{ color: "#ff4d4f" }}
                                    />
                                    <span>
                                      Delete All Files (
                                      {record.documents.length} files)
                                    </span>
                                  </div>
                                ),
                                onClick: () => handleDeleteAllFiles(record),
                              },
                              // Divider only if multiple files
                              {
                                type: "divider",
                              },
                            ]
                          : []),
                        // Individual file delete options
                        ...record.documents.map((doc: any, index: number) => ({
                          key: `delete-file-${index}`,
                          label: (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                padding: "4px 8px",
                                cursor: "pointer",
                                position: "relative",
                                minHeight: "24px",
                              }}
                              className="file-row-hover"
                            >
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "#666",
                                  flex: 1,
                                }}
                              >
                                {doc.docName}
                              </span>
                              <Icon
                                name="trash"
                                style={{
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  marginLeft: "8px",
                                }}
                                className="delete-icon"
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                  e.stopPropagation();
                                  handleDeleteFile(doc, index);
                                }}
                              />
                            </div>
                          ),
                          onClick: () => {
                            // Delete the individual file
                            handleDeleteFile(doc, index);
                          },
                        })),
                      ],
                    }}
                    placement="bottomRight"
                    trigger={["hover"]}
                  >
                    <span
                      style={{
                        cursor: "pointer",
                        color: "#ff4d4f",
                        fontSize: "18px",
                        display: "flex",
                        alignItems: "center",
                      }}
                      title="Delete Options"
                    >
                      <Icon name="trash" />
                    </span>
                  </Dropdown>
                </CancelAccess>
              )}
              {groupBy === "activities" && (
                <CancelAccess moduleName="Activities">
                  <Dropdown
                    menu={{
                      items: [
                        // Option to delete entire document record (only show if multiple files)
                        ...(record.documents.length > 1
                          ? [
                              {
                                key: "delete-all",
                                label: (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      color: "#ff4d4f",
                                      fontWeight: "500",
                                    }}
                                  >
                                    <Icon
                                      name="trash"
                                      style={{ color: "#ff4d4f" }}
                                    />
                                    <span>
                                      Delete All Files (
                                      {record.documents.length} files)
                                    </span>
                                  </div>
                                ),
                                onClick: () => handleDeleteAllFiles(record),
                              },
                              // Divider only if multiple files
                              {
                                type: "divider",
                              },
                            ]
                          : []),
                        // Individual file delete options
                        ...record.documents.map((doc: any, index: number) => ({
                          key: `delete-file-${index}`,
                          label: (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                padding: "4px 8px",
                                cursor: "pointer",
                                position: "relative",
                                minHeight: "24px",
                              }}
                              className="file-row-hover"
                            >
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "#666",
                                  flex: 1,
                                }}
                              >
                                {doc.docName}
                              </span>
                              <Icon
                                name="trash"
                                style={{
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  marginLeft: "8px",
                                }}
                                className="delete-icon"
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                  e.stopPropagation();
                                  handleDeleteFile(doc, index);
                                }}
                              />
                            </div>
                          ),
                          onClick: () => {
                            // Delete the individual file
                            handleDeleteFile(doc, index);
                          },
                        })),
                      ],
                    }}
                    placement="bottomRight"
                    trigger={["hover"]}
                  >
                    <span
                      style={{
                        cursor: "pointer",
                        color: "#ff4d4f",
                        fontSize: "18px",
                        display: "flex",
                        alignItems: "center",
                      }}
                      title="Delete Options"
                    >
                      <Icon name="trash" />
                    </span>
                  </Dropdown>
                </CancelAccess>
              )}
              {groupBy === "tasks" && (
                <CancelAccess moduleName="Tasks">
                  <Dropdown
                    menu={{
                      items: [
                        // Option to delete entire document record (only show if multiple files)
                        ...(record.documents.length > 1
                          ? [
                              {
                                key: "delete-all",
                                label: (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      color: "#ff4d4f",
                                      fontWeight: "500",
                                    }}
                                  >
                                    <Icon
                                      name="trash"
                                      style={{ color: "#ff4d4f" }}
                                    />
                                    <span>
                                      Delete All Files (
                                      {record.documents.length} files)
                                    </span>
                                  </div>
                                ),
                                onClick: () => handleDeleteAllFiles(record),
                              },
                              // Divider only if multiple files
                              {
                                type: "divider",
                              },
                            ]
                          : []),
                        // Individual file delete options
                        ...record.documents.map((doc: any, index: number) => ({
                          key: `delete-file-${index}`,
                          label: (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                padding: "4px 8px",
                                cursor: "pointer",
                                position: "relative",
                                minHeight: "24px",
                              }}
                              className="file-row-hover"
                            >
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "#666",
                                  flex: 1,
                                }}
                              >
                                {doc.docName}
                              </span>
                              <Icon
                                name="trash"
                                style={{
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  marginLeft: "8px",
                                }}
                                className="delete-icon"
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                  e.stopPropagation();
                                  handleDeleteFile(doc, index);
                                }}
                              />
                            </div>
                          ),
                          onClick: () => {
                            // Delete the individual file
                            handleDeleteFile(doc, index);
                          },
                        })),
                      ],
                    }}
                    placement="bottomRight"
                    trigger={["hover"]}
                  >
                    <span
                      style={{
                        cursor: "pointer",
                        color: "#ff4d4f",
                        fontSize: "18px",
                        display: "flex",
                        alignItems: "center",
                      }}
                      title="Delete Options"
                    >
                      <Icon name="trash" />
                    </span>
                  </Dropdown>
                </CancelAccess>
              )}
              {groupBy === "issues" && (
                <CancelAccess moduleName="Issues">
                  <Dropdown
                    menu={{
                      items: [
                        // Option to delete entire document record (only show if multiple files)
                        ...(record.documents.length > 1
                          ? [
                              {
                                key: "delete-all",
                                label: (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      color: "#ff4d4f",
                                      fontWeight: "500",
                                    }}
                                  >
                                    <Icon
                                      name="trash"
                                      style={{ color: "#ff4d4f" }}
                                    />
                                    <span>
                                      Delete All Files (
                                      {record.documents.length} files)
                                    </span>
                                  </div>
                                ),
                                onClick: () => handleDeleteAllFiles(record),
                              },
                              // Divider only if multiple files
                              {
                                type: "divider",
                              },
                            ]
                          : []),
                        // Individual file delete options
                        ...record.documents.map((doc: any, index: number) => ({
                          key: `delete-file-${index}`,
                          label: (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                padding: "4px 8px",
                                cursor: "pointer",
                                position: "relative",
                                minHeight: "24px",
                              }}
                              className="file-row-hover"
                            >
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "#666",
                                  flex: 1,
                                }}
                              >
                                {doc.docName}
                              </span>
                              <Icon
                                name="trash"
                                style={{
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  marginLeft: "8px",
                                }}
                                className="delete-icon"
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                  e.stopPropagation();
                                  handleDeleteFile(doc, index);
                                }}
                              />
                            </div>
                          ),
                          onClick: () => {
                            // Delete the individual file
                            handleDeleteFile(doc, index);
                          },
                        })),
                      ],
                    }}
                    placement="bottomRight"
                    trigger={["hover"]}
                  >
                    <span
                      style={{
                        cursor: "pointer",
                        color: "#ff4d4f",
                        fontSize: "18px",
                        display: "flex",
                        alignItems: "center",
                      }}
                      title="Delete Options"
                    >
                      <Icon name="trash" />
                    </span>
                  </Dropdown>
                </CancelAccess>
              )}
            </Space>
          );
        }

        // For single documents, show dropdowns with file name
        return (
          <Space size="small">
            <Dropdown
              menu={{
                items: [
                  {
                    key: "view",
                    label: (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Icon name="eye" style={{ color: "#4169E1" }} />
                        <span>{record.docName || record.customDocName}</span>
                      </div>
                    ),
                    onClick: () => window.open(record.filePath, "_blank"),
                  },
                ],
              }}
              placement="bottomRight"
              trigger={["hover"]}
            >
              <span
                style={{
                  cursor: "pointer",
                  color: "#4169E1",
                  fontSize: "18px",
                  display: "flex",
                  alignItems: "center",
                  marginRight: "8px",
                }}
                title="View Document"
              >
                <Icon name="eye" />
              </span>
            </Dropdown>

            <Dropdown
              menu={{
                items: [
                  {
                    key: "download",
                    label: (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Icon name="download" style={{ color: "#52c41a" }} />
                        <span>{record.docName || record.customDocName}</span>
                      </div>
                    ),
                    onClick: () =>
                      downloadDocument(
                        record.filePath,
                        record.docName || record.customDocName
                      ),
                  },
                ],
              }}
              placement="bottomRight"
              trigger={["hover"]}
            >
              <span
                style={{
                  cursor: "pointer",
                  color: "#52c41a",
                  fontSize: "18px",
                  display: "flex",
                  alignItems: "center",
                  marginRight: "8px",
                }}
                title="Download Document"
              >
                <Icon name="download" />
              </span>
            </Dropdown>

            {/* Add More Documents Button for Single Documents */}
            <span
              style={{
                cursor: "pointer",
                color: "#4169E1",
                fontSize: "18px",
                display: "flex",
                alignItems: "center",
                marginRight: "8px",
              }}
              title="Add More Documents to This Group"
              onClick={() => {
                // Set flag to indicate we're adding to an existing group
                setIsAddingToGroup(true);
                // Open the upload modal with pre-filled values in correct format
                form.setFieldsValue({
                  documents: [
                    {
                      linkType: record.linkType,
                      linkTo: record.linkTo,
                      project:
                        record.linkType === "project"
                          ? record.linkTo
                          : undefined,
                      phase:
                        record.linkType === "phase" ? record.linkTo : undefined,
                      activity:
                        record.linkType === "activity"
                          ? record.linkTo
                          : undefined,
                      task:
                        record.linkType === "task" ? record.linkTo : undefined,
                      issue:
                        record.linkType === "issue" ? record.linkTo : undefined,
                      docName: record.customDocName || record.docName,
                      remarks: record.remarks || "",
                      file: undefined,
                    },
                  ],
                });
                setIsModalVisible(true);
              }}
            >
              <Icon name="plus-circle" />
            </span>
            {groupBy === "projects" && (
              <CancelAccess moduleName="Projects">
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "delete",
                        label: (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <Icon name="trash" style={{ color: "#ff4d4f" }} />
                            <span style={{ fontSize: "12px", color: "#666" }}>
                              {record.docName || record.customDocName}
                            </span>
                          </div>
                        ),
                        onClick: () => handleDeleteDocument(record),
                      },
                    ],
                  }}
                  placement="bottomRight"
                  trigger={["hover"]}
                >
                  <span
                    style={{
                      cursor: "pointer",
                      color: "#ff4d4f",
                      fontSize: "18px",
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Delete Document"
                  >
                    <Icon name="trash" />
                  </span>
                </Dropdown>
              </CancelAccess>
            )}
            {groupBy === "phases" && (
              <CancelAccess moduleName="Phases">
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "delete",
                        label: (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <Icon name="trash" style={{ color: "#ff4d4f" }} />
                            <span style={{ fontSize: "12px", color: "#666" }}>
                              {record.docName || record.customDocName}
                            </span>
                          </div>
                        ),
                        onClick: () => handleDeleteDocument(record),
                      },
                    ],
                  }}
                  placement="bottomRight"
                  trigger={["hover"]}
                >
                  <span
                    style={{
                      cursor: "pointer",
                      color: "#ff4d4f",
                      fontSize: "18px",
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Delete Document"
                  >
                    <Icon name="trash" />
                  </span>
                </Dropdown>
              </CancelAccess>
            )}
            {groupBy === "activities" && (
              <CancelAccess moduleName="Activities">
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "delete",
                        label: (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <Icon name="trash" style={{ color: "#ff4d4f" }} />
                            <span style={{ fontSize: "12px", color: "#666" }}>
                              {record.docName || record.customDocName}
                            </span>
                          </div>
                        ),
                        onClick: () => handleDeleteDocument(record),
                      },
                    ],
                  }}
                  placement="bottomRight"
                  trigger={["hover"]}
                >
                  <span
                    style={{
                      cursor: "pointer",
                      color: "#ff4d4f",
                      fontSize: "18px",
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Delete Document"
                  >
                    <Icon name="trash" />
                  </span>
                </Dropdown>
              </CancelAccess>
            )}
            {groupBy === "tasks" && (
              <CancelAccess moduleName="Tasks">
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "delete",
                        label: (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <Icon name="trash" style={{ color: "#ff4d4f" }} />
                            <span style={{ fontSize: "12px", color: "#666" }}>
                              {record.docName || record.customDocName}
                            </span>
                          </div>
                        ),
                        onClick: () => handleDeleteDocument(record),
                      },
                    ],
                  }}
                  placement="bottomRight"
                  trigger={["hover"]}
                >
                  <span
                    style={{
                      cursor: "pointer",
                      color: "#ff4d4f",
                      fontSize: "18px",
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Delete Document"
                  >
                    <Icon name="trash" />
                  </span>
                </Dropdown>
              </CancelAccess>
            )}
            {groupBy === "issues" && (
              <CancelAccess moduleName="Issues">
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "delete",
                        label: (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <Icon name="trash" style={{ color: "#ff4d4f" }} />
                            <span style={{ fontSize: "12px", color: "#666" }}>
                              {record.docName || record.customDocName}
                            </span>
                          </div>
                        ),
                        onClick: () => handleDeleteDocument(record),
                      },
                    ],
                  }}
                  placement="bottomRight"
                  trigger={["hover"]}
                >
                  <span
                    style={{
                      cursor: "pointer",
                      color: "#ff4d4f",
                      fontSize: "18px",
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Delete Document"
                  >
                    <Icon name="trash" />
                  </span>
                </Dropdown>
              </CancelAccess>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Head title={"Documents"} />
      <Content>
        <FormProvider {...methods}>
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              maxWidth: "100vw",
            }}
          >
            <Card>
              <div>
                <div
                  style={{
                    minHeight: "87vh",
                    overflow: "hidden",
                    width: "100%",
                  }}
                >
                  <h5
                    style={{
                      margin: "0 0 16px 0",
                      color: "#262626",
                      fontSize: "20px",
                      fontWeight: 600,
                    }}
                  >
                    {projectName || "Documents"} / Documents
                  </h5>

                  <form>
                    <div
                      className="nk-ibx-head p-0 m-0 mt-1 w-100 d-flex justify-content-between align-items-center"
                      style={{ minHeight: "40px", border: "0px" }}
                    >
                      <div className="nk-ibx-head-actions w-45 ms-1">
                        <div className="nk-ibx-head-tools g-1 m-0 p-0 w-100 ">
                          <div className={`w-100 m-0 `}>
                            <SearchInput
                              name="search"
                              placeholder="Search documents..."
                              width="100%"
                              prefixIcon={<SearchOutlined />}
                              onDebouncedSearch={async (value: any) => {
                                // Only make API call if search value is not empty or has changed
                                if (value !== searchQuery) {
                                  setSearchQuery(value);
                                  setPagination({
                                    ...pagination,
                                    current: 1,
                                  });
                                  await fetchData({
                                    oldPage: 1,
                                    current: 1,
                                    sortOrder: "DESC",
                                    pageSize: pagination.pageSize.toString(),
                                    direction: "P",
                                    pageDiff: 0,
                                    paginationId: null,
                                    searchQuery: value,
                                  });
                                }
                              }}
                              debounceDelay={500}
                            />
                          </div>
                        </div>
                      </div>
                      <ul className="nk-ibx-head-tools g-1 m-0 p-0 ">
                        <li className="pt-0 pb-0">
                          {/* Group By Dropdown */}
                          <Dropdown
                            menu={{
                              items: getFilteredGroupByOptions().map(
                                (item: any) => ({
                                  key: item.key,
                                  label: item.label,
                                })
                              ),
                              onClick: (item) => setGroupBy(item.key),
                              selectedKeys: [groupBy],
                            }}
                          >
                            <a onClick={(e) => e.preventDefault()}>
                              <Space>
                                <Text
                                  style={{
                                    color: "#2557d6",
                                    fontWeight: 500,
                                    fontSize: "14px",
                                  }}
                                >
                                  {getLabel(groupByOptions, groupBy)}
                                </Text>
                                <DownOutlined style={{ color: "#2557d6" }} />
                              </Space>
                            </a>
                          </Dropdown>
                        </li>
                        <li>
                          {groupBy === "projects" &&
                            canAdd(accessibility, "Projects") && (
                              <Button
                                className="toggle"
                                color="primary"
                                size="sm"
                                onClick={showModal}
                              >
                                <Icon name={"plus"} />
                                <span>Add Document</span>
                              </Button>
                            )}
                          {groupBy === "phases" &&
                            canAdd(accessibility, "Phases") && (
                              <Button
                                className="toggle"
                                color="primary"
                                size="sm"
                                onClick={showModal}
                              >
                                <Icon name={"plus"} />
                                <span>Add Document</span>
                              </Button>
                            )}
                          {groupBy === "activities" &&
                            canAdd(accessibility, "Activities") && (
                              <Button
                                className="toggle"
                                color="primary"
                                size="sm"
                                onClick={showModal}
                              >
                                <Icon name={"plus"} />
                                <span>Add Document</span>
                              </Button>
                            )}
                          {groupBy === "tasks" &&
                            canAdd(accessibility, "Tasks") && (
                              <Button
                                className="toggle"
                                color="primary"
                                size="sm"
                                onClick={showModal}
                              >
                                <Icon name={"plus"} />
                                <span>Add Document</span>
                              </Button>
                            )}
                          {groupBy === "issues" &&
                            canAdd(accessibility, "Issues") && (
                              <Button
                                className="toggle"
                                color="primary"
                                size="sm"
                                onClick={showModal}
                              >
                                <Icon name={"plus"} />
                                <span>Add Document</span>
                              </Button>
                            )}
                        </li>
                      </ul>
                    </div>
                  </form>

                  <div className="card-inner card-inner-lg p-0">
                    <div className="content clearfix">
                      <div
                        style={{
                          width: "100%",
                          margin: "10px auto",
                        }}
                      >
                        <SkeletonTable
                          loading={loading}
                          columns={columns as SkeletonTableColumnsType[]}
                        >
                          <Table
                            rowKey="docId"
                            columns={columns}
                            dataSource={dataSource}
                            pagination={{
                              ...pagination,
                              showSizeChanger: true,
                              // showQuickJumper: true, // Commented out "Go to page" functionality
                            }}
                            onChange={tableOnChange}
                            bordered
                            size="middle"
                            scroll={{
                              x: 1100,
                            }}
                          />
                        </SkeletonTable>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </FormProvider>
      </Content>

      {/* Document Upload Drawer */}
      <Drawer
        title={
          <div
            style={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#1a1a1a",
            }}
          >
            Add New Document
          </div>
        }
        open={isModalVisible}
        onClose={handleCancel}
        width={800}
        placement="right"
        styles={{
          body: {
            padding: "24px",
            backgroundColor: "#fafafa",
          },
          header: {
            borderBottom: "1px solid #f0f0f0",
            padding: "16px",
          },
        }}
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              padding: "16px 0",
              borderTop: "1px solid #f0f0f0",
            }}
          >
            <Button
              onClick={handleCancel}
              style={{
                borderRadius: "6px",
                height: "36px",
              }}
            >
              Cancel
            </Button>
            {saveLoading ? (
              <Button
                color="primary"
                type="button"
                style={{
                  borderRadius: "6px",
                  height: "36px",
                  fontSize: "12px",
                  padding: "4px 12px",
                }}
              >
                <Spinner size="sm" color="light" />
              </Button>
            ) : (
              <Button
                className="toggle"
                color="primary"
                size="sm"
                onClick={handleOk}
                style={{
                  borderRadius: "6px",
                  height: "36px",
                  fontSize: "12px",
                  padding: "4px 12px",
                }}
              >
                <Icon name={"save"} />
                <span>Save Documents</span>
              </Button>
            )}
          </div>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            documents: [
              {
                linkType: undefined,
                linkTo: undefined,
                project: undefined,
                phase: undefined,
                docName: "",
                remarks: "",
                file: undefined,
                selectedProjectId: undefined,
              },
            ],
          }}
          onValuesChange={(changedValues) => {
            try {
            } catch (error) {
              console.error("Error in onValuesChange:", error);
            }
          }}
        >
          {/* Show document name info when adding to group */}
          {isAddingToGroup && (
            <div
              style={{
                marginBottom: "16px",
                padding: "12px 16px",
                backgroundColor: "#f6ffed",
                border: "1px solid #b7eb8f",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#52c41a",
                display: "flex",
                alignItems: "center",
              }}
            >
              <InfoCircleOutlined
                style={{ marginRight: "8px", fontSize: "16px" }}
              />
              Adding files to existing document:
              <strong style={{ marginLeft: "4px" }}>
                {form.getFieldValue(["documents", 0, "docName"])}
              </strong>
            </div>
          )}

          <div
            style={{
              border: "1px solid #e8e8e8",
              borderRadius: "8px",
              padding: "20px",
              marginBottom: "16px",
              backgroundColor: "#fff",
              position: "relative",
            }}
          >
            <Row gutter={[24, 16]}>
              <Col span={12}>
                <Form.Item
                  name={["documents", 0, "linkType"]}
                  label={
                    <span style={{ fontWeight: 500, color: "#262626" }}>
                      Link Type
                    </span>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please select link type",
                      validator: (_, value) => {
                        if (!value || value === undefined || value === "") {
                          return Promise.reject(
                            new Error("Please select link type")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Link Type"
                    style={{ borderRadius: "6px" }}
                    size="large"
                    disabled={
                      isAddingToGroup ||
                      groupBy === "projects" ||
                      groupBy === "phases" ||
                      groupBy === "activities" ||
                      groupBy === "tasks" ||
                      groupBy === "issues"
                    } // Disable when adding to group or when any groupBy is selected
                    onChange={(value) => handleLinkTypeChange(value, 0)}
                  >
                    {linkTypeOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                {/* Show Link To field only when NOT phase OR when groupBy is phases */}
                {(form.getFieldValue(["documents", 0, "linkType"]) !==
                  "phase" ||
                  groupBy === "phases") && (
                  <Form.Item
                    name={["documents", 0, "linkTo"]}
                    label={
                      <span style={{ fontWeight: 500, color: "#262626" }}>
                        Link To
                      </span>
                    }
                    rules={[
                      {
                        required: true,
                        message: "Please select link to",
                        validator: (_, value) => {
                          if (!value || value === undefined || value === "") {
                            return Promise.reject(
                              new Error("Please select link to")
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select Link To"
                      disabled={
                        isAddingToGroup ||
                        !form.getFieldValue(["documents", 0, "linkType"]) ||
                        form.getFieldValue(["documents", 0, "linkType"]) ===
                          "" ||
                        (0 === 0 && groupBy === "projects") // Disable when adding to group or only first document when pre-filled for projects
                      }
                      style={{ borderRadius: "6px" }}
                      size="large"
                      onChange={(value) => handleLinkToChange(value, 0)}
                    >
                      {getLinkToOptions(
                        form.getFieldValue(["documents", 0, "linkType"]) || "",
                        0
                      ).map((option) => (
                        <Option
                          key={option.value}
                          value={option.value}
                          disabled={
                            "disabled" in option ? option.disabled : false
                          }
                        >
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
              </Col>
            </Row>

            {/* Show Project and Phase fields when Phase is selected as Link Type AND NOT groupBy phases */}
            {form.getFieldValue(["documents", 0, "linkType"]) === "phase" &&
              groupBy !== "phases" && (
                <>
                  <Row gutter={[24, 16]}>
                    <Col span={12}>
                      <Form.Item
                        name={["documents", 0, "project"]}
                        label={
                          <span style={{ fontWeight: 500, color: "#262626" }}>
                            Project
                          </span>
                        }
                        rules={[
                          {
                            required: true,
                            message: "Please select project",
                          },
                        ]}
                      >
                        <Select
                          placeholder="Select Project"
                          style={{ borderRadius: "6px" }}
                          size="large"
                          disabled={isAddingToGroup || groupBy === "phases"} // Disable when adding to group or when Group By Phases is selected
                          onChange={(value) => handleLinkToChange(value, 0)}
                        >
                          {projectList.map((project: any) => (
                            <Option
                              key={project.projectId.toString()}
                              value={project.projectId.toString()}
                            >
                              {project.projectTitle}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={["documents", 0, "phase"]}
                        label={
                          <span style={{ fontWeight: 500, color: "#262626" }}>
                            Phase
                            {selectedProjectForPhase &&
                              phaseList.length === 0 && (
                                <span
                                  style={{
                                    color: "#ff4d4f",
                                    fontSize: "12px",
                                    marginLeft: "8px",
                                  }}
                                >
                                  (No phases available)
                                </span>
                              )}
                          </span>
                        }
                        rules={[
                          {
                            required: true,
                            message:
                              phaseList.length === 0 && selectedProjectForPhase
                                ? "This project has no phases. Please select a different project or link type."
                                : "Please select phase",
                          },
                        ]}
                      >
                        <Select
                          placeholder={
                            selectedProjectForPhase
                              ? phaseList.length > 0
                                ? "Select Phase"
                                : "No phases available for this project"
                              : "Select a project first"
                          }
                          disabled={
                            isAddingToGroup ||
                            !selectedProjectForPhase ||
                            phaseList.length === 0
                          }
                          style={{ borderRadius: "6px" }}
                          size="large"
                          notFoundContent={
                            phaseList.length === 0 && selectedProjectForPhase
                              ? "No phases found for this project"
                              : "No phases available"
                          }
                        >
                          {phaseList.map((phase: any) => (
                            <Option
                              key={phase.phaseId.toString()}
                              value={phase.phaseId.toString()}
                            >
                              {wbsName(phase.phaseName)}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}

            <Row gutter={[24, 16]}>
              <Col span={12}>
                <Form.Item
                  name={["documents", 0, "docName"]}
                  label={
                    <span style={{ fontWeight: 500, color: "#262626" }}>
                      Document Name
                    </span>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please enter document name",
                      validator: (_, value) => {
                        if (!value || value.trim() === "") {
                          return Promise.reject(
                            new Error("Please enter document name")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter document name"
                    style={{
                      borderRadius: "6px",
                      width: "100%",
                      height: "40px",
                    }}
                    size="large"
                    disabled={isAddingToGroup}
                    className={isAddingToGroup ? "disabled-doc-name" : ""}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={["documents", 0, "file"]}
                  label={
                    <span style={{ fontWeight: 500, color: "#262626" }}>
                      File Upload (Multiple)
                    </span>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please upload at least one file",
                      validator: (_, value) => {
                        if (
                          !value ||
                          !value.fileList ||
                          value.fileList.length === 0
                        ) {
                          return Promise.reject(
                            new Error("Please upload at least one file")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Upload
                    beforeUpload={() => false}
                    multiple={true}
                    maxCount={5}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                    listType="text"
                    showUploadList={{
                      showPreviewIcon: true,
                      showRemoveIcon: true,
                      showDownloadIcon: true,
                    }}
                    style={{ width: "100%", maxWidth: "100%" }}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      size="large"
                      style={{
                        height: "40px",
                        fontWeight: 500,
                        backgroundColor: "#ffffff",
                        borderColor: "#d9d9d9",
                        color: "#595959",
                        width: "100%",
                      }}
                    >
                      Choose Files
                    </Button>
                  </Upload>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[24, 16]}>
              <Col span={24}>
                <Form.Item
                  name={["documents", 0, "remarks"]}
                  label={
                    <span style={{ fontWeight: 500, color: "#262626" }}>
                      Remarks
                    </span>
                  }
                >
                  <TextArea
                    placeholder="Enter remarks (optional)"
                    rows={3}
                    style={{
                      borderRadius: "6px",
                      resize: "none",
                      border: "1px solid #d9d9d9",
                    }}
                    showCount
                    maxLength={500}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Professional "Supported Files" section */}
          <div
            style={{
              marginTop: "20px",
              padding: "16px",
              backgroundColor: "#f8f9fa",
              border: "1px solid #e9ecef",
              borderRadius: "6px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "#495057",
                fontSize: "13px",
              }}
            >
              <InfoCircleOutlined
                style={{ color: "#2557d6", fontSize: "14px" }}
              />
              <span>
                <strong>Supported file types:</strong> PDF, DOC, DOCX, XLS,
                XLSX, TXT, JPG, JPEG, PNG
              </span>
            </div>
          </div>
        </Form>
      </Drawer>
    </>
  );
};

export default DocumentPage;
